import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Badge,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Banner,
  useIndexResourceState,
  InlineStack,
  Box,
  Avatar,
} from '@shopify/polaris';
import {
  testimonialService,
  type Testimonial,
  type CreateTestimonialInput,
  type ContentStatus,
} from '../../services/content.service';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
];

const RATING_OPTIONS = [
  { label: '5 Stars', value: '5' },
  { label: '4 Stars', value: '4' },
  { label: '3 Stars', value: '3' },
  { label: '2 Stars', value: '2' },
  { label: '1 Star', value: '1' },
];

function renderStars(rating: number): string {
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export function TestimonialManagementPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [customerName, setCustomerName] = useState('');
  const [company, setCompany] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [text, setText] = useState('');
  const [rating, setRating] = useState('5');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [status, setStatus] = useState<ContentStatus>('ACTIVE');

  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
    useIndexResourceState(testimonials as unknown as {[key: string]: unknown}[]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await testimonialService.list({ limit: 100 });
      setTestimonials(result.data);
    } catch (err) {
      setError('Failed to load testimonials');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setCustomerName('');
    setCompany('');
    setPhotoUrl('');
    setText('');
    setRating('5');
    setDisplayOrder('0');
    setStatus('ACTIVE');
    setEditingTestimonial(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (testimonial: Testimonial) => {
    setEditingTestimonial(testimonial);
    setCustomerName(testimonial.customerName);
    setCompany(testimonial.company || '');
    setPhotoUrl(testimonial.photoUrl || '');
    setText(testimonial.text);
    setRating(testimonial.rating.toString());
    setDisplayOrder(testimonial.displayOrder.toString());
    setStatus(testimonial.status);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!customerName.trim() || !text.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const data: CreateTestimonialInput = {
        customerName: customerName.trim(),
        company: company.trim() || undefined,
        photoUrl: photoUrl.trim() || undefined,
        text: text.trim(),
        rating: parseInt(rating),
        displayOrder: parseInt(displayOrder) || 0,
        status,
      };

      if (editingTestimonial) {
        await testimonialService.update(editingTestimonial.id, data);
      } else {
        await testimonialService.create(data);
      }

      setShowModal(false);
      resetForm();
      await loadData();
    } catch (err) {
      setError('Failed to save testimonial');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await testimonialService.delete(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete testimonial');
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${selectedResources.length} testimonials?`)) return;

    try {
      await Promise.all(selectedResources.map((id) => testimonialService.delete(id)));
      clearSelection();
      await loadData();
    } catch (err) {
      setError('Failed to delete testimonials');
      console.error(err);
    }
  };

  const rowMarkup = testimonials.map((testimonial, index) => (
    <IndexTable.Row
      id={testimonial.id}
      key={testimonial.id}
      selected={selectedResources.includes(testimonial.id)}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center">
          <Avatar
            customer
            size="sm"
            name={testimonial.customerName}
            source={testimonial.photoUrl || undefined}
          />
          <div>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {testimonial.customerName}
            </Text>
            {testimonial.company && (
              <Text variant="bodySm" tone="subdued" as="p">
                {testimonial.company}
              </Text>
            )}
          </div>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodySm" as="span">
          {testimonial.text.slice(0, 80)}
          {testimonial.text.length > 80 ? '...' : ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span" tone="caution">
          {renderStars(testimonial.rating)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {testimonial.displayOrder}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={testimonial.status === 'ACTIVE' ? 'success' : undefined}>
          {testimonial.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => openEditModal(testimonial)}>
            Edit
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDelete(testimonial.id)}>
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const bulkActions = [
    {
      content: 'Delete selected',
      onAction: handleBulkDelete,
      destructive: true,
    },
  ];

  return (
    <Page
      title="Testimonials"
      primaryAction={{
        content: 'Add Testimonial',
        onAction: openCreateModal,
      }}
    >
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Box>
      )}

      <Card padding="0">
        <IndexTable
          resourceName={{ singular: 'testimonial', plural: 'testimonials' }}
          itemCount={testimonials.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          bulkActions={bulkActions}
          headings={[
            { title: 'Customer' },
            { title: 'Testimonial' },
            { title: 'Rating' },
            { title: 'Order' },
            { title: 'Status' },
            { title: 'Actions' },
          ]}
          loading={loading}
        >
          {rowMarkup}
        </IndexTable>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
        primaryAction={{
          content: editingTestimonial ? 'Save Changes' : 'Create Testimonial',
          loading: saving,
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowModal(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Customer Name"
              value={customerName}
              onChange={setCustomerName}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Company"
              value={company}
              onChange={setCompany}
              autoComplete="off"
            />
            <TextField
              label="Photo URL"
              value={photoUrl}
              onChange={setPhotoUrl}
              autoComplete="off"
              helpText="URL to customer's photo (optional)"
            />
            <TextField
              label="Testimonial Text"
              value={text}
              onChange={setText}
              multiline={4}
              autoComplete="off"
              requiredIndicator
            />
            <Select
              label="Rating"
              options={RATING_OPTIONS}
              value={rating}
              onChange={setRating}
            />
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={setDisplayOrder}
              autoComplete="off"
              helpText="Lower numbers appear first"
            />
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => setStatus(value as ContentStatus)}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
